import { pushDataLayer, redactTokenPath } from "@/lib/analytics";

/** Cookie names, fields and ft_* keys are shared with giftsforgood.com and GTM — do not rename. */
interface Touch {
  source: string;
  medium: string;
  campaign: string;
  content: string;
  term: string;
  gclid: string;
  li_fat_id: string;
  via: string;
  referrer: string;
  landing_page: string;
  ts: string;
  channel?: string;
}

// Sign-in and payment providers hand the user back to us; they are hops, not a source.
const AUTH_AND_PAYMENT_HOSTS = [
  "accounts.google.com",
  "login.microsoftonline.com",
  "login.live.com",
  "checkout.stripe.com",
];
const AI_HOSTS = [
  "chatgpt.com",
  "chat.openai.com",
  "perplexity.ai",
  "claude.ai",
  "gemini.google.com",
  "copilot.microsoft.com",
  "you.com",
];
const SEARCH_HOSTS = ["google.", "bing.com", "duckduckgo.com", "yahoo.", "ecosia.org"];

function channel(t: Touch): string {
  if (t.via) return "affiliate";
  if (t.gclid || (t.source === "google" && t.medium === "cpc")) return "paid_search";
  if (t.li_fat_id || (t.source === "linkedin" && /paid|cpc/.test(t.medium))) return "paid_social";
  if (t.medium === "email") return "email";
  if (t.medium === "ai" || AI_HOSTS.indexOf(t.referrer) !== -1) return "ai_search";
  if (t.medium === "organic_social") return "organic_social";
  if (!t.source && SEARCH_HOSTS.some((h) => t.referrer.indexOf(h) !== -1)) return "organic_search";
  if (!t.source && t.referrer) return "referral";
  if (t.source) return t.medium || "other";
  return "direct";
}

function setCookie(name: string, obj: Touch, days: number): void {
  // biome-ignore lint/suspicious/noDocumentCookie: the marketing site reads these same cookies.
  document.cookie = `${name}=${encodeURIComponent(JSON.stringify(obj))}; domain=.giftsforgood.com; path=/; max-age=${days * 86400}; SameSite=Lax; Secure`;
}

function getCookie(name: string): Touch | null {
  const m = document.cookie.match(`(?:^|; )${name}=([^;]*)`);
  if (!m?.[1]) return null;
  try {
    return JSON.parse(decodeURIComponent(m[1])) as Touch;
  } catch {
    return null;
  }
}

function isOwnHost(host: string): boolean {
  return host === "giftsforgood.com" || host.endsWith(".giftsforgood.com");
}

function referrerHost(): string {
  try {
    return document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, "") : "";
  } catch {
    return "";
  }
}

/**
 * First/last-touch attribution from the marketing team's GA4 spec (GFG-424). Runs once per page
 * load, before GTM, so GTM can attach the ft_* values to sign_up.
 */
export function recordAttribution(): void {
  if (location.pathname.startsWith("/sso-callback")) return;
  const p = new URLSearchParams(location.search);
  const refHost = referrerHost();
  if (isOwnHost(refHost) || AUTH_AND_PAYMENT_HOSTS.indexOf(refHost) !== -1) return;

  const t: Touch = {
    source: p.get("utm_source") || "",
    medium: p.get("utm_medium") || "",
    campaign: p.get("utm_campaign") || "",
    content: p.get("utm_content") || "",
    term: p.get("utm_term") || "",
    gclid: p.get("gclid") || "",
    li_fat_id: p.get("li_fat_id") || "",
    via: p.get("via") || "",
    referrer: refHost,
    landing_page: redactTokenPath(location.pathname),
    ts: new Date().toISOString(),
  };
  t.channel = channel(t);

  const hasSignal = t.source || t.gclid || t.li_fat_id || t.via || t.referrer;
  if (!hasSignal && getCookie("gfg_lt")) return;
  setCookie("gfg_lt", t, 90);
  if (!getCookie("gfg_ft")) setCookie("gfg_ft", t, 90);
  const ft = getCookie("gfg_ft") || t;

  pushDataLayer({
    ft_source: ft.source,
    ft_medium: ft.medium,
    ft_campaign: ft.campaign,
    ft_channel: ft.channel,
    ft_landing_page: ft.landing_page,
  });
}
